import { createId } from "../../app/runtimeContext";
import type { ToolRegistry } from "../tools/toolRegistry";
import type { CyanIntelligenceRepository } from "./repositories";
import type { ContextSource, ProductContext } from "./liveTypes";

export class ContextAssemblyService {
  constructor(
    private readonly repository: CyanIntelligenceRepository,
    private readonly tools: ToolRegistry,
  ) {}

  async assemble(input: {
    context: ProductContext;
    conversationId: string;
    limit?: number;
  }): Promise<ContextSource[]> {
    const goals = await this.repository.listGoals({
      productId: input.context.productId,
    });
    const plans = await this.repository.listPlans({
      productId: input.context.productId,
    });
    const conversations = await this.repository.listConversations({
      productId: input.context.productId,
    });
    const toolSources = this.tools
      .list()
      .slice(0, 4)
      .map<ContextSource>((tool) => ({
        sourceType: "tool" as const,
        sourceId: tool.name,
        title: tool.name,
        relevanceScore: tool.connected ? 0.7 : 0.45,
        confidence: 0.8,
        productId: input.context.productId,
        reasonSelected: "Tool registry status informs execution readiness.",
        freshness: "current-runtime",
        verificationStatus: "verified" as const,
      }));
    const sources: ContextSource[] = [
      {
        sourceType: "product" as const,
        sourceId: input.context.productId,
        title: input.context.productName,
        relevanceScore: 1,
        confidence: 1,
        productId: input.context.productId,
        reasonSelected: "Selected product context.",
        freshness: "current-session",
        verificationStatus: "verified" as const,
      },
      ...conversations.map((conversation) => ({
        sourceType: "conversation" as const,
        sourceId: conversation.id,
        title: conversation.title,
        relevanceScore: conversation.id === input.conversationId ? 0.95 : 0.6,
        confidence: 0.85,
        productId: conversation.productId,
        reasonSelected: "Conversation belongs to selected product.",
        freshness: conversation.updatedAt,
        verificationStatus: "verified" as const,
      })),
      ...goals.map((goal) => ({
        sourceType: "goal" as const,
        sourceId: goal.id,
        title: goal.title,
        relevanceScore: 0.8,
        confidence: 0.8,
        productId: goal.productId,
        reasonSelected: "Goal belongs to selected product.",
        freshness: goal.updatedAt,
        verificationStatus: "verified" as const,
      })),
      ...plans.map((plan) => ({
        sourceType: "plan" as const,
        sourceId: plan.id,
        title: plan.summary,
        relevanceScore: 0.75,
        confidence: 0.78,
        productId: plan.productId,
        reasonSelected: "Plan belongs to selected product.",
        freshness: plan.createdAt,
        verificationStatus: "verified" as const,
      })),
      ...toolSources,
    ].slice(0, input.limit ?? 12);
    for (const source of sources)
      await this.repository.saveContextSource({
        ...source,
        sourceId: source.sourceId || createId("context_source"),
      });
    return sources;
  }
}
